import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { usePublicOrder } from "../context/PublicOrderContext";
import api from "../utils/api";

import CustomerNavbar from "./CustomerNavbar";
import CategorySidebar from "./CategorySidebar";
import ProductSection from "./ProductSection";
import OrderPanel from "./OrderPanel";
import { toast } from "react-toastify";
import ModifierCustomiser from "./ModifierCustomiser";

const MenuPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Fall back to the order saved for this restaurant if the link has no ?orderId
  const orderId = searchParams.get("orderId") || localStorage.getItem(`orderId_${slug}`);

  /* ================= CONTEXT ================= */

  const {
    cart,
    addToCart,
    clearCart,
    increaseQty,
    decreaseQty,
    removeItem,
    updateItemQty,
  } = usePublicOrder();

  /* ================= STATE ================= */

  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loading, setLoading] = useState(false);

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [customisingProduct, setCustomisingProduct] = useState(null);
  
  // Customer Info Modal State
  const [showCustomerPrompt, setShowCustomerPrompt] = useState(false);
  const [customerName, setCustomerName] = useState("");
  const [customerMobile, setCustomerMobile] = useState("");

  const { orderInfo, setOrderInfo, setOrderId, restaurant } = usePublicOrder();

  const isKitchenPaused = restaurant && restaurant.isAcceptingOrders === false;

  useEffect(() => {
    const fetchOrder = async () => {
      if (!orderId || orderInfo) return;

      try {
        const res = await api.get(
          `/api/public/restaurant/${slug}/order/${orderId}`,
        );

        setOrderInfo(res.data);
        setOrderId(res.data._id);
      } catch (err) {
        console.error("Failed to restore order", err);
        navigate(`/r/${slug}`);
      }
    };

    fetchOrder();
  }, [orderId, slug]);

  /* ================= PROTECT ROUTE ================= */

  useEffect(() => {
    const storedOrderId = localStorage.getItem(`orderId_${slug}`);
    const mode = searchParams.get("mode");

    if (!orderId && !storedOrderId && mode !== "view") {
      navigate(`/r/${slug}`);
    }
  }, [orderId, slug, navigate, searchParams]);

  /* ================= FETCH CATEGORIES ================= */

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await api.get(`/api/public/restaurant/${slug}/categories`);

        const fetched = res.data.categories || [];
        setCategories(fetched);
        setSelectedCategory(null);
      } catch (err) {
        const message = err.response?.data?.message || "Category fetch failed";
        toast.error(message);
      }
    };

    fetchCategories();
  }, [slug]);

  /* ================= PLACE ORDER ================= */

  const handleConfirmOrderClick = () => {
    if (cart.length === 0) return;
    if (!orderId) {
      toast.error("Your table session has expired. Please scan the QR code again.");
      return;
    }
    if (isKitchenPaused) {
      toast.error("The kitchen is currently busy and not accepting new orders.");
      return;
    }
    setShowCustomerPrompt(true);
  };

  const placeOrder = async () => {
    if (!orderId || cart.length === 0) return;

    try {
      setShowCustomerPrompt(false);
      setLoading(true);

      await api.patch(`/api/public/restaurant/${slug}/order/${orderId}/items`, {
        customerName: customerName.trim() || undefined,
        customerMobile: customerMobile.trim() || undefined,
        items: cart.map((item) => ({
          itemId: item.id,
          qty: item.qty,
          price: item.price,
          variant: item.selectedVariant ? { name: item.selectedVariant.name, price: item.selectedVariant.price } : undefined,
          addOns: item.selectedAddOns ? item.selectedAddOns.map(a => ({ name: a.name, price: a.price })) : [],
        })),
      });

      clearCart();

      navigate(`/r/${slug}/success`, {
        state: {
          orderId,
          orderNumber: orderInfo?.orderNumber,
          orderType: orderInfo?.orderType,
          floorName: orderInfo?.floorName,
          sectionName: orderInfo?.sectionName,
          sectionType: orderInfo?.sectionType,
          unitNumber: orderInfo?.unitNumber,
          roomType: orderInfo?.roomType,
        },
      });
    } catch (err) {
      const message = err.response?.data?.message || "Order failed";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = (item, selectedVariant = null, selectedAddOns = []) => {
    if (isKitchenPaused) {
      toast.error("Kitchen is paused. Cannot add items to cart.");
      return;
    }
    const mode = searchParams.get("mode");
    if (mode === "view") {
      toast.info("Dine-In tables are full! Switch to Takeaway mode at the side/bottom to place an order.", {
        position: "top-center",
        autoClose: 3000
      });
      return;
    }
    addToCart(item, selectedVariant, selectedAddOns);
  };

  const handleSwitchToTakeaway = async () => {
    try {
      setLoading(true);
      const res = await api.post(
        `/api/public/restaurant/${slug}/order`,
        { orderType: "DIRECT" }
      );
      setOrderInfo(res.data);
      setOrderId(res.data._id);
      sessionStorage.removeItem("table_lock_active");
      clearCart();
      navigate(`/r/${slug}/menu?orderId=${res.data._id}`);
    } catch (err) {
      console.error("Failed to switch to takeaway order", err);
      toast.error("Failed to initialize Takeaway order.");
    } finally {
      setLoading(false);
    }
  };

  /* ================= RENDER ================= */

  return (
    <div className="min-h-screen bg-gray-50">
      {/* ================= NAVBAR ================= */}
      <CustomerNavbar
        slug={slug}
        cartCount={cart.reduce((sum, item) => sum + item.qty, 0)}
        phoneNumber="8570996916"
        onMenuClick={() => setIsSidebarOpen(true)}
        onCartClick={() => setIsCartOpen(true)}
      />

      {isKitchenPaused && (
        <div className="bg-red-500 text-white text-center py-2.5 px-4 font-bold text-sm shadow-sm sticky top-0 z-40">
          ⚠️ Our kitchen is currently busy. We are temporarily pausing new digital orders.
        </div>
      )}

      {/* Offset for fixed navbar */}
      <div className={isKitchenPaused ? "pt-2" : "pt-8"}>
        {/* ================= DESKTOP VIEW ================= */}
        <div className="hidden lg:grid grid-cols-12 h-[calc(100vh-64px)]">
          {/* LEFT SIDEBAR */}
          <div className="col-span-2 p-2 border-r bg-white overflow-y-auto h-full">
            <CategorySidebar
              slug={slug}
              selectedCategory={selectedCategory}
              onSelectCategory={setSelectedCategory}
            />
          </div>

          {/* CENTER PRODUCTS */}
          <div className="col-span-7 overflow-y-auto">
            <ProductSection
              slug={slug}
              selectedCategory={selectedCategory}
              addToCart={handleAddToCart}
              onCustomise={setCustomisingProduct}
            />
          </div>

          {/* RIGHT ORDER PANEL */}
          <div className="col-span-3 border-l p-2 bg-white overflow-y-hidden">
            <OrderPanel
              cart={cart}
              increaseQty={increaseQty}
              decreaseQty={decreaseQty}
              removeItem={removeItem}
              updateItemQty={updateItemQty}
              placeOrder={handleConfirmOrderClick}
              loading={loading}
              mode={searchParams.get("mode")}
              onSwitchToTakeaway={handleSwitchToTakeaway}
              takeawayEnabled={restaurant?.orderCapabilities?.takeawayEnabled}
              platformFee={restaurant?.platformFee}
            />
          </div>
        </div>

        {/* ================= MOBILE VIEW ================= */}
        <div className="lg:hidden min-h-[calc(100vh-64px)]">
          {/* Categories (vertical) */}
          {isSidebarOpen && (
            <div className="fixed inset-0 z-50 flex">
              {/* Sidebar FIRST */}
              <div className="w-[80%] bg-white h-full shadow-2xl p-4 animate-slideInLeft relative">
                <button
                  onClick={() => setIsSidebarOpen(false)}
                  className="absolute top-4 right-4 text-xl font-bold text-red-600 hover:text-red-800"
                >
                  ✕
                </button>
                <CategorySidebar
                  slug={slug}
                  selectedCategory={selectedCategory}
                  onSelectCategory={(id) => {
                    setSelectedCategory(id);
                    setIsSidebarOpen(false);
                  }}
                />
              </div>

              {/* Blur SECOND */}
              <div
                className="flex-1 backdrop-blur-md bg-white/20"
                onClick={() => setIsSidebarOpen(false)}
              />
            </div>
          )}

          {/* Cart Drawer */}
          {isCartOpen && (
            <div className="fixed inset-0 z-50 flex flex-row">
              {/* Blur Layer */}
              <div
                className="flex-1 backdrop-blur-md bg-white/20"
                onClick={() => setIsCartOpen(false)}
              />

              {/* Cart Panel */}
              <div className="w-[90%] sm:w-[70%] md:w-[60%] bg-white h-full shadow-2xl p-4 animate-slideInRight relative overflow-y-auto">
                <button
                  onClick={() => setIsCartOpen(false)}
                  className="absolute top-4 right-4 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-red-100 text-lg font-bold text-red-600 hover:bg-red-200"
                >
                  ✕
                </button>
                <OrderPanel
                  cart={cart}
                  increaseQty={increaseQty}
                  decreaseQty={decreaseQty}
                  removeItem={removeItem}
                  updateItemQty={updateItemQty}
                  placeOrder={handleConfirmOrderClick}
                  loading={loading}
                  mode={searchParams.get("mode")}
                  onSwitchToTakeaway={handleSwitchToTakeaway}
                />
              </div>
            </div>
          )}

          <ProductSection
            slug={slug}
            selectedCategory={selectedCategory}
            addToCart={handleAddToCart}
            onCustomise={setCustomisingProduct}
          />
          
          {/* Mobile Floating Cart Button */}
          {cart.length > 0 && !isCartOpen && (
            <button
              onClick={() => setIsCartOpen(true)}
              className="fixed bottom-6 right-6 z-40 bg-teal-600 text-white p-4 rounded-full shadow-[0_8px_30px_rgb(0,0,0,0.12)] flex items-center justify-center animate-bounce hover:bg-teal-700 transition"
            >
              <i className="fas fa-shopping-cart text-xl"></i>
              <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full border-2 border-white">
                {cart.reduce((sum, item) => sum + item.qty, 0)}
              </span>
            </button>
          )}
        </div>
      </div>

      <ModifierCustomiser
        product={customisingProduct}
        isOpen={!!customisingProduct}
        onClose={() => setCustomisingProduct(null)}
        onAdd={handleAddToCart}
      />

      {/* Customer Info Modal */}
      {showCustomerPrompt && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fadeIn">
          <div className="bg-white w-full max-w-sm rounded-2xl shadow-2xl p-6 relative animate-slideInUp">
            <button
              onClick={() => setShowCustomerPrompt(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
            <div className="text-center mb-6">
              <div className="w-12 h-12 bg-teal-50 rounded-full flex items-center justify-center text-teal-600 mx-auto mb-3">
                <i className="fas fa-user text-xl"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900">Checkout Details</h3>
              <p className="text-sm text-gray-500 mt-1">Enter your details to help us serve you better (Optional)</p>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">Name</label>
                <input
                  type="text"
                  placeholder="Your Name"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 focus:bg-white transition"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">Mobile Number</label>
                <input
                  type="tel"
                  placeholder="Your Mobile Number"
                  value={customerMobile}
                  onChange={(e) => setCustomerMobile(e.target.value)}
                  className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 focus:bg-white transition"
                />
              </div>
              <button
                onClick={placeOrder}
                className="w-full mt-6 bg-teal-600 hover:bg-teal-700 text-white font-bold py-3 rounded-xl shadow-md transition transform active:scale-95 flex justify-center items-center gap-2"
              >
                Proceed to Order
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MenuPage;
